/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-underscore-dangle */
// eslint-disable-next-line spaced-comment
/// <reference types="@types/google.maps" />
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { isFuture } from 'date-fns';
import styled from 'styled-components';
import { breakpoints, message, theme, MARKER_SYMBOLS } from 'components';
import { AuthContext, LayoutContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import { GOOGLE_MAP_PLACEHOLDER_COORDS } from '@/constants';
import { debounce } from 'lodash';
import TourEditContext from '../TourEditContext';
import InfoWindows from './InfoWindows';
import {
  MAP_BOUNDS_PADDING,
  DEFAULT_INFO_HEIGHT,
  DEFAULT_INFO_WIDTH,
  DISPLAY_UNITS,
  ERROR_MESSAGES,
  TOURS_MAP_ID,
} from '../constants';
import { isWaypointOptimized, reorderOptimizedTour } from '../utils';
import { OptimizeStatus, RouteInfo } from '../types';
import { useShouldFetchRoute, useWindowHeight } from '../hooks';

type WindowWithMap = Window & typeof globalThis & { mapLoaded: boolean };

const initialPadding: google.maps.Padding = {
  top: MAP_BOUNDS_PADDING,
  bottom: MAP_BOUNDS_PADDING,
  right: MAP_BOUNDS_PADDING,
  left: MAP_BOUNDS_PADDING,
};

const TourMapView: React.FC = () => {
  const { mapLoaded } = window as WindowWithMap;
  const geoMap = useRef<HTMLDivElement>(null);
  const directionsService = useRef<google.maps.DirectionsService>();
  const directionsRenderer = useRef<google.maps.DirectionsRenderer>();
  const [directionsPolyEncoding, setDirectionsPolyEncoding] =
    useState<string>();
  const [googleMap, setGoogleMap] = useState<google.maps.Map>();
  const [mapBound, setMapBound] = useState<google.maps.LatLngBounds>();
  const [mapPadding, setMapPadding] =
    useState<google.maps.Padding>(initialPadding);
  const [savedMarker, setSavedMarker] = useState<google.maps.Marker[]>([]);
  const [savedInfoWindow, setSavedInfoWindow] = useState<
    google.maps.InfoWindow[]
  >([]);
  const {
    setOptimizeStatus,
    tourItems,
    setTourItems,
    setRouteInfo,
    travelMode,
    optimizeFlag,
    setOptimizeFlag,
  } = useContext(TourEditContext);
  const { auth } = useContext(AuthContext);
  const infoWindowRefs = useRef<HTMLDivElement[]>([]);
  const infoWindowSizeRefs = useRef<HTMLDivElement[]>([]);
  const { md } = useBreakpoint();
  const isMobileOnFirstRender = window.innerWidth < breakpoints.md;

  const filteredTourItems = useMemo(() => 
    // Only use items with coordinates, in some cases, coordinates are missing
    // due to bad listing address / google API failure during feed pulls
     tourItems.filter(item => Boolean(item.coordinates))
  , [tourItems]);

  const { topElementHeights } = useContext(LayoutContext);
  const height = useWindowHeight();
  const mobileHeight = useMemo(() => 
    // on mobile, first anchor point of floating nav takes up window.innerHeight * 0.33
    // so map should take up the remainder - header height (with some slight overlap)
     height * 0.7 - topElementHeights.bbAssume
  , [topElementHeights.bbAssume, height]);

  const mapRenderKey = useShouldFetchRoute(filteredTourItems);

  useEffect(() => {
    if (mapLoaded) {
      const gMap = new google.maps.Map(geoMap.current, {
        center: new google.maps.LatLng(
          GOOGLE_MAP_PLACEHOLDER_COORDS[0],
          GOOGLE_MAP_PLACEHOLDER_COORDS[1],
        ),
        zoom: 16,
        mapTypeId: 'terrain',
        // Disable user control
        disableDefaultUI: true,
        clickableIcons: false,
        disableDoubleClickZoom: true,
        keyboardShortcuts: false,
        maxZoom: 17,
        zoomControl: true,
        zoomControlOptions: {
          position: isMobileOnFirstRender
            ? google.maps.ControlPosition.RIGHT_CENTER
            : google.maps.ControlPosition.LEFT_BOTTOM,
        },
        ...(isMobileOnFirstRender && { draggable: false }),
      });
      setGoogleMap(gMap);

      const dirService = new google.maps.DirectionsService();
      const dirRenderer = new google.maps.DirectionsRenderer({
        hideRouteList: true,
        draggable: false,
        map: gMap,
        preserveViewport: true,
        suppressInfoWindows: true,
        suppressMarkers: true,
        polylineOptions: {
          clickable: false,
          strokeColor: theme.blue6,
          strokeWeight: 5,
        },
      });
      directionsService.current = dirService;
      directionsRenderer.current = dirRenderer;
    }
  }, [mapLoaded]);

  useEffect(() => {
    const markerArray: google.maps.Marker[] = [];
    const infoWindowArray: google.maps.InfoWindow[] = [];

    if (directionsRenderer.current) {
      const currentMap = directionsRenderer.current.getMap();
      if (!currentMap && googleMap) {
        directionsRenderer.current.setMap(googleMap);
      }
    }

    // Not using above memoized value, since we need both the original
    // and the filtered tourItem values
    const itemsWithCoords = tourItems.filter(item => Boolean(item.coordinates));

    if (googleMap && itemsWithCoords.length) {
      const positions = itemsWithCoords.map(
        item =>
          new google.maps.LatLng(item.coordinates[1], item.coordinates[0]),
      );

      const bounds =
        (itemsWithCoords.length > 1 && mapBound) ||
        positions.reduce(
          (newBounds, position) => newBounds.extend(position),
          new google.maps.LatLngBounds(),
        );

      const boundCenterLng = bounds.getCenter().lng();
      let furthestRight = boundCenterLng;
      let furthestLeft = boundCenterLng;
      const newPadding = { ...initialPadding };

      positions.forEach((position, posIndex) => {
        MARKER_SYMBOLS.forEach((symbol, symIndex) => {
          const marker = new google.maps.Marker({
            position,
            icon: {
              path: 0,
              fillOpacity: 1,
              strokeWeight: 0,
              anchor: new google.maps.Point(0, 0),
              ...symbol,
            },
            clickable: false,
          });
          markerArray.push(marker);

          if (symIndex === 0) {
            // Fragile way of calculating offsets depending on G MAP API
            // and our internal CSS factors
            // TODO: Logic needs to be improved after MVP
            const directionVector = position.lng() >= boundCenterLng ? 1 : -1;

            if (position.lng() >= furthestRight) {
              furthestRight = position.lng();
              if (md)
                newPadding.right =
                  infoWindowSizeRefs.current[posIndex].offsetWidth +
                  MAP_BOUNDS_PADDING;
            } else if (position.lng() < furthestLeft) {
              furthestLeft = position.lng();
              if (md)
                newPadding.left =
                  infoWindowSizeRefs.current[posIndex].offsetWidth +
                  MAP_BOUNDS_PADDING;
            }
            const margin =
              directionVector *
              (infoWindowSizeRefs.current[posIndex].offsetWidth / 2 +
                DEFAULT_INFO_WIDTH);

            const infoWindow = new google.maps.InfoWindow({
              content: infoWindowRefs.current[posIndex],
              pixelOffset: new google.maps.Size(margin, DEFAULT_INFO_HEIGHT),
              disableAutoPan: true,
            });
            infoWindowArray.push(infoWindow);
          }
        });
      });

      setMapBound(bounds);
      setMapPadding(newPadding);

      if (directionsService.current && itemsWithCoords.length >= 2) {
        const requestArray: google.maps.DirectionsRequest[] = [];
        // Iterate over index 1 to length-1 to optimize
        for (let index = 1; index < positions.length; index++) {
          const requestTest: google.maps.DirectionsRequest = {
            avoidFerries: true,
            avoidTolls: true,
            origin: positions[0],
            destination: positions[index],
            provideRouteAlternatives: false,
            travelMode,
            unitSystem: DISPLAY_UNITS[auth.org.country],
            optimizeWaypoints: optimizeFlag,
          };
          if (itemsWithCoords[0].time && isFuture(itemsWithCoords[0].time)) {
            requestTest.drivingOptions = {
              departureTime: itemsWithCoords[0].time,
              trafficModel: google.maps.TrafficModel.BEST_GUESS,
            };
          }
          if (itemsWithCoords.length >= 3) {
            const segmentedPositions: google.maps.LatLng[] = [
              ...positions.slice(1, index),
              ...positions.slice(index + 1),
            ];
            requestTest.waypoints = segmentedPositions.map(position => ({
              location: position,
            }));
            // }
          }
          requestArray.push(requestTest);
        }

        const request: google.maps.DirectionsRequest = {
          avoidFerries: true,
          avoidTolls: true,
          origin: positions[0],
          destination: positions[positions.length - 1],
          provideRouteAlternatives: false,
          travelMode,
          unitSystem: DISPLAY_UNITS[auth.org.country],
          optimizeWaypoints: optimizeFlag,
        };
        if (itemsWithCoords[0].time && isFuture(itemsWithCoords[0].time)) {
          request.drivingOptions = {
            departureTime: itemsWithCoords[0].time,
            trafficModel: google.maps.TrafficModel.BEST_GUESS,
          };
        }

        if (itemsWithCoords.length >= 3) {
          request.waypoints = positions
            .slice(1, -1)
            .map(position => ({ location: position }));
        }

        if (optimizeFlag) {
          const optimizationPromises = requestArray.map(
            requestItem =>
              new Promise<google.maps.DirectionsResult>(resolve => {
                directionsService.current?.route(
                  requestItem,
                  (result, status) => {
                    if (status === 'OK' && result) {
                      resolve(result);
                    }
                  },
                );
              }),
          );
          (async () => {
            try {
              const response = await Promise.all(optimizationPromises);
              const durationArray = response.map(optimizationResult => {
                const durationLeg = optimizationResult?.routes[0].legs.reduce(
                  (acc, currentLeg) =>
                    acc +
                    (currentLeg?.duration?.value
                      ? currentLeg.duration.value
                      : 0),
                  0,
                );
                return durationLeg;
              });
              const optimizedPath =
                response[durationArray.indexOf(Math.min(...durationArray))];

              // Rearrange the destination and waypoints based on optimized path
              const newOrder = reorderOptimizedTour(
                tourItems,
                optimizedPath.routes[0].waypoint_order,
                // Add one as the position of the optimization happens from the second item and on
                durationArray.indexOf(Math.min(...durationArray)) + 1,
              );
              setOptimizeFlag(false);
              setTourItems(newOrder);
              setOptimizeStatus(OptimizeStatus.OPTIMIZED);
              if (
                optimizedPath.routes?.[0]?.bounds &&
                optimizedPath.routes?.[0]?.overview_polyline !==
                  directionsPolyEncoding
              ) {
                setMapBound(optimizedPath.routes?.[0]?.bounds);
                setDirectionsPolyEncoding(
                  optimizedPath.routes?.[0]?.overview_polyline,
                );
              }
            } catch {
              setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);
              message.error(ERROR_MESSAGES.INVALID_REQUEST);
            }
          })();
        } else {
          directionsService.current.route(request, (result, status) => {
            if (status === 'OK' && result) {
              const isOptimized = isWaypointOptimized(
                result.routes?.[0]?.waypoint_order,
              );
              if (!isOptimized) setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);

              directionsRenderer.current?.setDirections(result);
              // In between Waypoint info
              if (result.routes?.[0]?.legs.length) {
                const newRouteInfo: Record<string, RouteInfo> = {};
                result.routes[0].legs.forEach(
                  ({ distance, duration }, index) => {
                    newRouteInfo[itemsWithCoords[index].id] = {
                      distance: distance.text,
                      duration: duration.text,
                    };
                  },
                );
                setRouteInfo(newRouteInfo);
              }

              if (
                result.routes?.[0]?.bounds &&
                result.routes?.[0]?.overview_polyline !== directionsPolyEncoding
              ) {
                setMapBound(result.routes?.[0]?.bounds);
                setDirectionsPolyEncoding(
                  result.routes?.[0]?.overview_polyline,
                );
              }
            } else {
              setOptimizeStatus(OptimizeStatus.NOT_OPTIMIZED);
              message.error(ERROR_MESSAGES[status]);
            }
          });
        }
      } else if (directionsRenderer.current) {
        // Meaning, tourItems changed with only one item left
        // Clear the directions render
        directionsRenderer.current.setMap(null);
        setRouteInfo({});
        setDirectionsPolyEncoding('');
      }
    }

    setSavedMarker(markerArray);
    setSavedInfoWindow(infoWindowArray);
  }, [
    mapRenderKey,
    googleMap,
    auth.org.country,
    travelMode,
    optimizeFlag,
    setTourItems,
    setOptimizeStatus,
    setRouteInfo,
    setOptimizeFlag,
    md,
  ]);

  // handle map bounds change
  useEffect(() => {
    if (mapBound && googleMap) {
      // fitBounds must be called after element size updated or mapBound changed
      const resizeListener = debounce(() => {
        googleMap.fitBounds(mapBound, mapPadding);
      }, 250);
      const resizeObserver = new ResizeObserver(resizeListener);
      const resizeObservee = geoMap.current;

      resizeObserver.observe(resizeObservee);

      return () => {
        resizeListener.cancel();
        resizeObserver.unobserve(resizeObservee);
      };
    }
  }, [googleMap, mapBound, mapPadding]);

  // handle rendering of marker && infoWindow
  useEffect(() => {
    if (typeof directionsPolyEncoding !== 'string') return;

    if (savedInfoWindow.length && md) {
      savedInfoWindow.forEach((infoWindow, index) => {
        // use ts-ignore, because .open() method is referencing wrong type package (@types/googlemaps instead of @types/google.maps)
        infoWindow.open({
          // @ts-ignore
          anchor: savedMarker[index * 2],
          map: googleMap,
          shouldFocus: false,
        });
      });
    }
    if (savedMarker.length) {
      savedMarker.forEach(marker => {
        marker.setMap(googleMap);
      });
    }
    // Clean up marker and info window
    return () => {
      if (savedInfoWindow.length) {
        savedInfoWindow.forEach(infoWindow => {
          infoWindow.close();
        });
      }
      if (savedMarker.length) {
        savedMarker.forEach(marker => {
          marker.setMap(null);
        });
      }
    };
  }, [directionsPolyEncoding, googleMap, md]);

  return (
    <>
      <StyledTourMapContainer $md={md} $mobileHeight={mobileHeight}>
        <StyledMapDiv id={TOURS_MAP_ID} ref={geoMap} />

        {filteredTourItems.map((item, index) => (
          // Here, can't use the 'key' prop since GMAP will take this content
          // into its DOM element
          <div key={`tour-info-window-${index}-${item.id}`}>
            <InfoWindows inputRef={infoWindowRefs} item={item} index={index} />
            <InfoWindows
              inputRef={infoWindowSizeRefs}
              item={item}
              index={index}
              forSizeOnly
            />
          </div>
        ))}
      </StyledTourMapContainer>
    </>
  );
};

export default TourMapView;

// Note that this is the only way to customize internal info window content,
// But it is fragile as there is a risk of G Map internal class name change.
const StyledTourMapContainer = styled.div<{
  $md: boolean;
  $mobileHeight: number;
}>`
  background-color: #ccc;

  ${({ $md, $mobileHeight }) => `${$md ? '' : `height: ${$mobileHeight}px;`}`};

  .gm-style-iw-t::after {
    content: none !important;
  }

  .gm-style-iw.gm-style-iw-c {
    padding: 0 !important;
    background-color: transparent;
    box-shadow: none;

    > button {
      display: none !important;
    }

    .gm-style-iw-d {
      padding: 0 !important;
      overflow: hidden !important;
    }
  }
`;

const StyledMapDiv = styled.div`
  width: 100%;
  height: 100%;
`;
